import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { playerAPI } from '../services/api';
import toast from 'react-hot-toast';

const BRAND = { blue: '#043663', gray: '#E5E7EB', lightBg: '#f8fafc' };

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  z-index: 10050;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0;
  @media (min-width: 640px) {
    align-items: center;
    padding: 24px;
  }
`;

const Modal = styled.div`
  background: #fff;
  width: 100%;
  max-width: 520px;
  max-height: min(88vh, 640px);
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  box-shadow: 0 -8px 40px rgba(0, 0, 0, 0.12);
  @media (min-width: 640px) {
    border-radius: 16px;
    max-height: min(80vh, 620px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  }
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid ${BRAND.gray};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

const HeaderText = styled.div`
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
`;

const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: #64748b;
  line-height: 1.35;
`;

const CloseBtn = styled.button`
  flex-shrink: 0;
  border: none;
  background: #f1f5f9;
  color: #475569;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  &:hover {
    background: #e2e8f0;
  }
`;

const MessagesBox = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.1rem;
  min-height: 200px;
`;

const Message = styled.div`
  margin-bottom: 0.75rem;
  text-align: ${(p) => (p.$isMe ? 'right' : 'left')};
`;

const Bubble = styled.div`
  display: inline-block;
  max-width: 88%;
  padding: 0.5rem 0.75rem;
  border-radius: 12px;
  background: ${(p) => (p.$isMe ? BRAND.blue : BRAND.lightBg)};
  color: ${(p) => (p.$isMe ? '#fff' : '#0f172a')};
  font-size: 0.9375rem;
  word-break: break-word;
`;

const Time = styled.div`
  font-size: 0.7rem;
  color: #94a3b8;
  margin-top: 0.2rem;
`;

const Footer = styled.div`
  flex-shrink: 0;
  padding: 0.75rem 1rem 1rem;
  border-top: 1px solid ${BRAND.gray};
`;

const Form = styled.form`
  display: flex;
  gap: 0.5rem;
`;
const Input = styled.input`
  flex: 1;
  padding: 0.55rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${BRAND.gray};
  font-size: 0.9375rem;
`;
const SendBtn = styled.button`
  padding: 0.55rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  background: ${BRAND.blue};
  color: #fff;
  border: none;
  cursor: pointer;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

/**
 * Modal DM thread using player conversation APIs (join-request / discovery chats).
 Does not navigate to /player/messages.
 */
export default function PlayerDmChatModal({ isOpen, conversationId, title, subtitle, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const bottomRef = useRef(null);

  const effectiveId = isOpen && conversationId ? conversationId : null;

  const { data: messagesRes, refetch: refetchMessages } = useQuery(
    ['conversationMessages', effectiveId],
    () => playerAPI.getConversationMessages(effectiveId),
    {
      enabled: !!effectiveId,
      refetchInterval: isOpen ? 5000 : false,
    }
  );
  const messages = messagesRes?.data ?? [];

  useEffect(() => {
    if (!isOpen || !effectiveId) return undefined;
    playerAPI.postConversationViewing(effectiveId).catch(() => {});
    playerAPI
      .markNotificationsReadByConversation(effectiveId)
      .then(() => {
        queryClient.invalidateQueries(['playerNotificationCount']);
        queryClient.invalidateQueries(['playerNotificationCount', 'messages']);
        queryClient.invalidateQueries(['playerNotificationsUnreadSummary']);
        queryClient.invalidateQueries(['playerNotifications']);
        queryClient.invalidateQueries(['playerConversations']);
      })
      .catch(() => {});
    return undefined;
  }, [isOpen, effectiveId, queryClient]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const sendMessage = useMutation(() => playerAPI.sendMessage(effectiveId, body), {
    onSuccess: () => {
      setBody('');
      refetchMessages();
      queryClient.invalidateQueries(['playerConversations']);
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || t('playerConversationPage.toastSendFailed')),
  });

  useEffect(() => {
    if (!isOpen) setBody('');
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!body.trim() || !effectiveId) return;
    sendMessage.mutate();
  };

  return (
    <Overlay role="presentation" onClick={onClose}>
      <Modal role="dialog" aria-modal="true" aria-labelledby="player-dm-chat-title" onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderText>
            <Title id="player-dm-chat-title">{title || t('playerTeamsPage.chat')}</Title>
            {subtitle ? <Subtitle>{subtitle}</Subtitle> : null}
          </HeaderText>
          <CloseBtn type="button" onClick={onClose} aria-label={t('playerConversationPage.back')}>
            ×
          </CloseBtn>
        </Header>
        <MessagesBox>
          {messages.length === 0 && (
            <p style={{ color: '#94a3b8', margin: 0 }}>{t('playerConversationPage.noMessages')}</p>
          )}
          {messages.map((m) => (
            <Message key={m.id} $isMe={m.isMe}>
              <Bubble $isMe={m.isMe}>{m.body}</Bubble>
              <Time>{new Date(m.createdAt).toLocaleString()}</Time>
            </Message>
          ))}
          <div ref={bottomRef} />
        </MessagesBox>
        <Footer>
          <Form onSubmit={handleSubmit}>
            <Input
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={t('playerConversationPage.placeholder')}
              maxLength={2000}
            />
            <SendBtn type="submit" disabled={sendMessage.isLoading || !body.trim()}>
              {t('playerConversationPage.send')}
            </SendBtn>
          </Form>
        </Footer>
      </Modal>
    </Overlay>
  );
}
